import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Tab,
  Tabs,
} from '@mui/material';
import { DateTime } from 'luxon';
import isEqual from 'lodash/isEqual';
import { useForm } from 'react-hook-form';

import type {
  TripLeg,
  TripLegType,
  Reservation,
} from '@/data/models/reservation.model';
import type { ServicePlan } from '@/data/models/service-plan.model';
import type { Passenger } from '@/data/models/passenger.model';
import { TransportationAttribute } from '@/data/models/transportation-attribute.model';

import {
  buildEmptyReservationFormValues,
  MEMBER_FORM_PHONE_FIELDS,
  ReservationFormValues,
  ReservationFormInput,
} from './schema/reservationForm.schema';

import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { ReservationFormDebug } from './hooks/useDebugReservationMachines';

import { usePortalContext } from '@/contexts/PortalProvider';

import * as lookupSlice from '@/data/lookup.slice';
import * as uiSlice from '@/data/ui.slice';
import * as accountingSlice from '@/data/accounting.slice';
import * as reservationSlice from '@/data/reservation.slice';
import { fetchPassengerPreferencesAsync } from '@/data/passenger.slice';

import reservationUtils from '@/services/reservationUtils';
import {
  ReservationFormMachinesProvider,
  useReservationFormMachines,
} from './ReservationFormMachinesContext';
import { getGurneyDataFromReservationFormSnapshot } from './machines/reservationFormMain.machine';
import { clearTripLegScoreCache } from './machines/runBundledValidation';
import { useTripLegValidationFormApplier } from './hooks/useTripLegValidationFormApplier';
import { useReservationFormResolver } from './hooks/useReservationFormResolver';
import { mergeTripLegConfidenceResponsesIntoReservationPayload } from './machines/tripLegConfidence';

import RequestOverrideDialog from './prompts/RequestOverrideDialog';
import { ReservationTripLegForm } from './ReservationTripLegForm';
import { useClientPortalPickupOffsetBlock } from './hooks/useClientPortalPickupOffsetBlock';
import { useDebouncedReservationActionSelectors } from './hooks/useDebouncedReservationActionSelectors';
import { useFieldActorSnapshot } from './hooks/useFieldActorSnapshot';
import { LevelOfCareContext } from './machines/levelOfCare.machine';
import { syncReservationFormMachinesFromValues } from './utils/syncReservationFormMachinesFromValues';
import {
  mergeAggregatedWithLiveForm,
  pickSecondaryAttributeFieldsFromForm,
} from './utils/getAggregatedFormValues';
import { ReservationFormSubmitProgress } from './ReservationFormSubmitProgress';
import TabPanel from '../TabPanel';
import { ReservationMemberForm } from './ReservationMemberForm';

const PREFIX = 'AddTripLegDialog';

const classes = {
  pages: `${PREFIX}-pages`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  '& .MuiDialogTitle-root, & .MuiDialogActions-root, & .MuiLinearProgress-root':
    {
      flexShrink: 0,
    },
  '& .MuiDialogContent-root': {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
  },
  [`& .${classes.pages}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
  },
}));

function AddTripLegDialogContent({
  form,
  resetServerFieldErrors,
  stateMachineDebugEnabled,
  transportationAttributes,
  open,
  sequence,
  tripType,
  passenger,
  reservationReferenceID,
  onClose,
}: {
  form: ReturnType<
    typeof useForm<ReservationFormInput, unknown, ReservationFormValues>
  >;
  /** Clears the persisted server errors; call with every `form.reset`. */
  resetServerFieldErrors: () => void;
  stateMachineDebugEnabled: boolean;
  transportationAttributes: TransportationAttribute[];
  open: boolean;
  sequence: number;
  tripType: TripLegType;
  passenger: Passenger;
  reservationReferenceID: string;
  onClose: () => void;
}) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const savedTripLeg = useAppSelector(reservationSlice.selectSavedTripLeg);
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const [isFormHydrated, setIsFormHydrated] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const { isMemberPortal } = usePortalContext();

  const { mainSnapshot, fieldRefs, mainActor } = useReservationFormMachines();
  const levelOfCareSnap = useFieldActorSnapshot(fieldRefs.levelOfCare);
  const { isCareLevelMismatch } = levelOfCareSnap.context as LevelOfCareContext;

  const watchedTrips = form.watch('trips');
  const topLevelFormWatch = form.watch([
    'AllowOverride',
    'OverrideFirstName',
    'OverrideLastName',
    'OverrideReason',
    'OverrideServicePlan',
    'MemberHomePhone',
    'MemberMobilePhone',
    'AllowText',
    'genericErrors',
    'AlternateContact',
    'AlternateContactType',
    'AlternateContactFirstName',
    'AlternateContactLastName',
    'AlternateContactPhone',
    'AdditionalPassengers',
    'AdditionalPassengerCount',
    'PassengerNoteTypes',
    'DriverNoteTypes',
    'RequestedTransportationProviderID',
    'ShowOtherInputs',
  ]);
  const tripLegCount = watchedTrips?.length ?? 0;
  const hasCareLevelMismatch = isCareLevelMismatch
    .slice(0, tripLegCount)
    .some(Boolean);

  const mergedValues = useMemo(
    () => mergeAggregatedWithLiveForm(mainSnapshot.context, form.getValues()),
    [mainSnapshot.context, watchedTrips, topLevelFormWatch],
  );

  /**
   * Keep tripLegMergedValues in sync from the dialog shell so Next/Done stay
   * accurate even when the other tab is unmounted.
   */
  useLayoutEffect(() => {
    if (!isFormHydrated) {
      return;
    }
    const existing = mainActor.getSnapshot().context.tripLegMergedValues;
    if (existing !== undefined && isEqual(existing, mergedValues)) {
      return;
    }
    mainActor.send({
      type: 'TRIP_LEG_MERGED_VALUES',
      mergedValues,
    });
  }, [mainActor, mergedValues, isFormHydrated]);

  const {
    tripLegNextButtonDisabled,
    requestOverrideButtonDisabled,
    editTripLegDoneButtonDisabled,
  } = useDebouncedReservationActionSelectors(
    mainSnapshot,
    {
      isMemberPortal,
      hasCareLevelMismatch,
    },
    {
      isFormHydrated,
      isMemberPortal,
      hasCareLevelMismatch,
    },
  );
  // The pickup lead-time message is not part of the Zod schema that gates Done.
  // ReservationTripLegForm only disables its inner Next button with this hook.
  const pickupOffsetBlocksDone = useClientPortalPickupOffsetBlock(
    form,
    fieldRefs.tripLegEstimatedPickupRefs,
  );

  const initKey = open
    ? `add:${tripType}:${passenger.passengerUUID}:${reservationReferenceID}:${transportationAttributes.length}`
    : '';
  const lastSyncedInitKeyRef = useRef<string | null>(null);
  const mainActorRef = useRef(mainActor);
  const fieldRefsRef = useRef(fieldRefs);
  mainActorRef.current = mainActor;
  fieldRefsRef.current = fieldRefs;

  useLayoutEffect(() => {
    if (!open) {
      lastSyncedInitKeyRef.current = null;
      setIsFormHydrated(false);
      return;
    }
    if (lastSyncedInitKeyRef.current === initKey) {
      setIsFormHydrated(true);
      return;
    }
    lastSyncedInitKeyRef.current = initKey;

    const baseValues = buildEmptyReservationFormValues(
      passenger,
      transportationAttributes,
    );
    const values = {
      ...baseValues,
      tripType,
      trips: baseValues.trips.map((trip) => ({
        ...trip,
        tripLegType: tripType,
      })),
    };

    resetServerFieldErrors();
    form.reset(values, { keepErrors: false });
    syncReservationFormMachinesFromValues(
      mainActorRef.current,
      fieldRefsRef.current,
      values,
    );
    setIsFormHydrated(true);
  }, [
    open,
    initKey,
    form,
    tripType,
    passenger,
    transportationAttributes,
    resetServerFieldErrors,
  ]);

  const handleClose = () => {
    setTabIndex(0);
    clearTripLegScoreCache();
    dispatch(reservationSlice.reset());
    onClose();
  };

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleNext = (evt: any) => {
    evt.preventDefault();
    setTabIndex(tabIndex + 1);
  };

  const handleOverrideOpen = () => {
    mainActor.send({ type: 'OVERRIDE_OPEN' });
  };

  const saveTrips = (formValues: ReservationFormValues) => {
    const machineSnapshot = mainActor.getSnapshot();
    const machineContext = machineSnapshot.context;
    const gurneyData = getGurneyDataFromReservationFormSnapshot(
      machineSnapshot,
      formValues,
    );

    let { reservation } =
      reservationUtils.getCreateReservationPayloadFromReservationForm(
        passenger.clientBC,
        passenger.passengerUUID!,
        formValues,
        reservationUtils.getPassengerPhone(passenger),
        gurneyData,
      );

    if (machineContext.featureFlags.addressValidationEnabled) {
      reservation = mergeTripLegConfidenceResponsesIntoReservationPayload(
        reservation,
        machineContext.confidenceByTripIndex,
      ) as Reservation;
    }

    const newTripLeg = reservation.tripLegs[0];
    newTripLeg.tripLegSequence = sequence + 1;
    newTripLeg.metadata = {
      ...newTripLeg.metadata,
      originalTripLegSequence: sequence + 1,
    };
    newTripLeg.reservationReferenceID = reservationReferenceID;
    const passengerPhone = reservationUtils.getPassengerPhone(passenger);
    newTripLeg.pickUpPhone = newTripLeg.pickUpPhone ?? passengerPhone;
    newTripLeg.dropOffPhone = newTripLeg.dropOffPhone ?? passengerPhone;
    dispatch(
      reservationSlice.addTripLegAsync(
        reservationReferenceID,
        newTripLeg,
        false,
      ),
    );
  };

  const save = (_data: ReservationFormValues) => {
    if (pickupOffsetBlocksDone) return;
    const live = form.getValues();
    const merged = mergeAggregatedWithLiveForm(
      mainActor.getSnapshot().context,
      {
        ...live,
        ...pickSecondaryAttributeFieldsFromForm(
          live as Record<string, unknown>,
        ),
      },
    );
    const formValues = {
      ...merged,
      trips: merged.trips.map((trip) => ({
        ...trip,
        tripLegType: merged.tripType,
      })),
    };
    saveTrips(formValues);
  };

  useEffect(() => {
    if (open && saveSuccessMessage && savedTripLeg) {
      handleClose();
    }
  }, [open, saveSuccessMessage, savedTripLeg]);

  return (
    <>
      <ReservationFormSubmitProgress />
      <DialogTitle>{t('reservation.addTripLeg')}</DialogTitle>
      <DialogContent>
        {!isMemberPortal && (
          <Tabs
            indicatorColor="primary"
            textColor="primary"
            value={tabIndex}
            onChange={handleTabChange}>
            <Tab label={t('reservation.tripDetails')} data-testid="trip-tab" />
            <Tab
              label={t('reservation.memberDetails')}
              data-testid="member-tab"
            />
          </Tabs>
        )}
        <Box className={classes.pages}>
          {!isFormHydrated ? (
            <LinearProgress />
          ) : (
            <>
              {stateMachineDebugEnabled && <ReservationFormDebug form={form} />}
              {!isMemberPortal && (
                <>
                  <TabPanel value={tabIndex} index={0}>
                    <ReservationTripLegForm
                      form={form}
                      isAdding={true}
                      onNext={handleNext}
                    />
                  </TabPanel>
                  <TabPanel value={tabIndex} index={1}>
                    <ReservationMemberForm
                      isEditing={true}
                      form={form}
                      transportationAttributes={transportationAttributes ?? []}
                    />
                  </TabPanel>
                </>
              )}
              {isMemberPortal && (
                <TabPanel value={tabIndex} index={0}>
                  <ReservationTripLegForm
                    form={form}
                    isAdding={true}
                    onNext={handleNext}
                  />
                </TabPanel>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="contained"
          disabled={tripLegNextButtonDisabled || pickupOffsetBlocksDone}
          onClick={form.handleSubmit(save)}>
          {t('common.done')}
        </Button>
        <Button
          color="secondary"
          variant="contained"
          disabled={mainSnapshot.matches('submitting')}
          onClick={() => handleClose()}>
          {t('common.cancel')}
        </Button>
        {!isMemberPortal && (
          <Button
            color="secondary"
            variant="contained"
            disabled={requestOverrideButtonDisabled}
            onClick={() => handleOverrideOpen()}>
            {t('reservation.requestOverride')}
          </Button>
        )}
      </DialogActions>
    </>
  );
}

interface AddTripLegDialogProps {
  sequence: number;
  reservationReferenceID: string;
  tripLeg?: TripLeg;
  tripType: TripLegType;
  passenger: Passenger;
  servicePlans: ServicePlan[];
  open: boolean;
  onClose: () => void;
}

const AddTripLegDialog = ({
  sequence,
  reservationReferenceID,
  tripType,
  passenger,
  servicePlans,
  open,
  onClose,
}: AddTripLegDialogProps) => {
  const dispatch = useAppDispatch();

  const { __environment__ } = window as any;
  const stateMachineDebugEnabled = Boolean(
    __environment__?.REACT_APP_STATE_MACHINE_DEBUG,
  );

  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );

  const formDefaults = useMemo(() => {
    const baseValues = buildEmptyReservationFormValues(
      passenger,
      transportationAttributes,
    );
    return {
      ...baseValues,
      tripType,
      trips: baseValues.trips.map((trip) => ({
        ...trip,
        tripLegType: tripType,
      })),
    };
  }, [tripType, passenger?.passengerUUID, transportationAttributes]);

  const {
    resolver,
    serverFieldErrorsRef,
    bindControl,
    resetServerFieldErrors,
  } = useReservationFormResolver();
  const form = useForm<ReservationFormInput, unknown, ReservationFormValues>({
    resolver,
    defaultValues: formDefaults,
    mode: 'onBlur',
  });
  bindControl(form.control);

  const pickUpTime = form.watch('trips.0.pickupDateTime');
  const appointmentDate = form.watch('trips.0.appointmentDate');

  // Member tab phone fields validate via the resolver on blur/submit; exclude
  // them from post-apply phone revalidation while the trip-leg tab is active.
  const applyTripLegValidationRef = useTripLegValidationFormApplier(
    form,
    () => ({ fieldsToExclude: MEMBER_FORM_PHONE_FIELDS }),
    // Optional-chained: ReservationList mounts this dialog (closed) before the
    // passenger record loads, so the prop can be null despite its type.
    passenger?.clientBC,
    passenger?.servicePlans?.[0]?.servicePlanUUID,
    serverFieldErrorsRef,
  );

  const tripLegServiceDate =
    (pickUpTime instanceof DateTime && pickUpTime.isValid
      ? pickUpTime.toISODate()
      : null) ??
    (appointmentDate instanceof DateTime && appointmentDate.isValid
      ? appointmentDate.toISODate()
      : null) ??
    (typeof appointmentDate === 'string' && appointmentDate.trim()
      ? appointmentDate
      : null) ??
    undefined;
  const overridableServicePlans = servicePlans.filter(
    ({ effectiveStartDate, effectiveEndDate }) =>
      tripLegServiceDate != null &&
      effectiveStartDate <= tripLegServiceDate &&
      (!effectiveEndDate || tripLegServiceDate <= effectiveEndDate),
  );

  useEffect(() => {
    dispatch(lookupSlice.fetchTransportationAttributeMapAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('timeZone'));
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegPassengerNoteType'));
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegDriverNoteType'));
  }, [dispatch]);

  useEffect(() => {
    if (!open || !passenger?.passengerUUID) {
      return;
    }
    // Care-level mismatch messaging needs preferences in Redux; parent pages
    // that open this dialog (trip details / reservation list) may not fetch them.
    dispatch(
      fetchPassengerPreferencesAsync(
        passenger.passengerUUID,
        passenger.clientBC,
      ),
    );
  }, [open, passenger?.passengerUUID, passenger?.clientBC, dispatch]);

  return (
    <ReservationFormMachinesProvider
      clientBC={passenger?.clientBC ?? ''}
      passengerUUID={passenger?.passengerUUID ?? ''}
      passengerPhone={
        passenger ? reservationUtils.getPassengerPhone(passenger) : undefined
      }
      applyTripLegValidationResultsToForm={() =>
        applyTripLegValidationRef.current
      }
      getPickupTimeZoneForTrip={(tripIndex) =>
        form.getValues('trips')[tripIndex]?.pickupTimeZone || undefined
      }
      getAddressStructsForTrip={(tripIndex) => {
        const trip = form.getValues('trips')[tripIndex];
        return {
          pickup: trip?.pickupAddressStruct,
          dropoff: trip?.dropoffAddressStruct,
        };
      }}>
      <StyledDialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <AddTripLegDialogContent
          form={form}
          resetServerFieldErrors={resetServerFieldErrors}
          stateMachineDebugEnabled={stateMachineDebugEnabled}
          transportationAttributes={transportationAttributes ?? []}
          open={open}
          sequence={sequence}
          tripType={tripType}
          passenger={passenger}
          reservationReferenceID={reservationReferenceID}
          onClose={onClose}
        />
      </StyledDialog>
      <RequestOverrideDialog
        servicePlans={overridableServicePlans}
        reservationForm={form}
      />
    </ReservationFormMachinesProvider>
  );
};

export { AddTripLegDialog };
